import PlanCard from "./PlanCard";
import { CatalogPlan } from "@/services/stripe";
import { crawlPageLimitFor } from "@/utils/crawlLimits";
import { monitorLimitShort } from "@/utils/monitorLimits";
import { formatCount, formatDay, formatMb, formatMoney, perThousand } from "@/utils/billingFormat";
import { planRank } from "./types";

interface Props {
  catalog: CatalogPlan[];
  currentPlan: string;
  pendingPlan: { name: string; effectiveAt: string } | null;
  ending: boolean;
  busyPlan: string | null;
  onChoose: (planName: string) => void;
}

const DESCRIPTIONS: Record<string, string> = {
  Free: "A great way to explore Spidra for light tasks and testing.",
  Starter: "Ideal for individuals and small projects starting with web scraping.",
  Builder: "Best for developers and small teams scaling up their scraping tasks.",
  Pro: "Optimized for teams and power users with high-volume scraping needs.",
};

const SUPPORT: Record<string, string> = {
  Free: "Community",
  Starter: "Community",
  Builder: "Email",
  Pro: "Priority",
};

// "50,000", "1.25M": keeps the credits note to two lines on the narrow four-card row.
const compact = (n: number) =>
  n >= 1_000_000 ? `${Number((n / 1_000_000).toFixed(2))}M` : formatCount(n);

// Every feature is on every plan, so a card lists only what changes with volume, in the same rows on every card.
function specsFor(plan: CatalogPlan): [string, string][] {
  const credits = plan.includedCredits ?? 0;
  const isFree = plan.name === "Free";
  return [
    ["Per 1,000 credits", isFree || credits <= 0 ? "Free" : perThousand(plan.priceCents, credits)],
    ["Proxy bandwidth", `${formatMb(plan.proxyBandwidthMb)}${isFree ? " once" : ""}`],
    ["Pages per crawl", formatCount(crawlPageLimitFor(plan.name))],
    ["Monitors", monitorLimitShort(plan.name)],
    ["Top-ups", isFree ? "No" : "Yes"],
    ["Support", SUPPORT[plan.name] ?? "Email"],
  ];
}

export default function PlanOptions({
  catalog,
  currentPlan,
  pendingPlan,
  ending,
  busyPlan,
  onChoose,
}: Props) {
  const currentRank = planRank(currentPlan);
  // Free only appears while you're on it: there's nothing to switch to.
  const plans = catalog.filter(p => p.name !== "Free" || currentPlan === "Free");
  // Your own paid plan gets the highlighted card. On Free, Builder is highlighted as the usual pick.
  const highlighted = currentPlan === "Free" ? "Builder" : currentPlan;

  return (
    // Hairline grid: the cards share their borders, as on spidra.io/pricing.
    <div
      className={`grid grid-cols-1 gap-px overflow-hidden rounded-card border border-line bg-line shadow-card sm:grid-cols-2 ${plans.length === 4 ? "xl:grid-cols-4" : "xl:grid-cols-3"}`}
    >
      {plans.map((plan, i) => {
        const rank = planRank(plan.name);
        const isCurrent = plan.name === currentPlan;
        const isPending = pendingPlan?.name === plan.name;
        const credits = plan.includedCredits ?? 0;
        const isFree = plan.name === "Free";

        let actionLabel: string;
        let actionKind: "upgrade" | "downgrade" | "status";
        if (isCurrent) {
          actionLabel = ending ? "Ends at renewal" : "Current plan";
          actionKind = "status";
        } else if (isPending) {
          actionLabel = `Starts ${formatDay(pendingPlan!.effectiveAt)}`;
          actionKind = "status";
        } else if (rank > currentRank) {
          actionLabel = currentPlan === "Free" ? `Choose ${plan.name}` : `Upgrade to ${plan.name}`;
          actionKind = "upgrade";
        } else {
          actionLabel = `Switch to ${plan.name}`;
          actionKind = "downgrade";
        }

        // With an odd number of plans on two columns, the last card spans both so no empty cell shows.
        const spanLast = plans.length % 2 === 1 && i === plans.length - 1;
        return (
          <div key={plan.name} className={spanLast ? "sm:col-span-2 xl:col-span-1" : undefined}>
            <PlanCard
              title={plan.name}
              price={formatMoney(plan.priceCents, plan.currency)}
              subtext={isFree ? "one time" : "month"}
              description={DESCRIPTIONS[plan.name]}
              credits={`${formatCount(credits)} credits`}
              creditsNote={isFree ? "once" : "a month"}
              subline={`About ${formatCount(credits)} pages or ${compact(credits * 10)} search results`}
              specs={specsFor(plan)}
              highlighted={plan.name === highlighted}
              current={isCurrent}
              actionLabel={actionLabel}
              actionKind={actionKind}
              isLoading={busyPlan === plan.name}
              disabled={!!busyPlan}
              onSelect={() => onChoose(plan.name)}
            />
          </div>
        );
      })}
    </div>
  );
}
